import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import WhatsAppSticky from "@/components/landing/WhatsAppSticky";
import LeadDialog from "@/components/landing/LeadDialog";
import Reveal from "@/components/landing/Reveal";
import { fetchSiteContent } from "@/lib/api";
import { setPageMeta } from "@/lib/pageMeta";

const assetUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const bgStyle = (u) => (u ? { backgroundImage: `url(${assetUrl(u)})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined);

export default function OurStoryPage() {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState({ open: false, type: "meeting" });
  const [siteContent, setSiteContent] = useState({});
  const openMeeting = useCallback(() => setDialog({ open: true, type: "meeting" }), []);
  const openBulk = useCallback(() => setDialog({ open: true, type: "bulk" }), []);
  const closeDialog = useCallback(() => setDialog((d) => ({ ...d, open: false })), []);
  const handlers = useMemo(() => ({ openMeeting, openBulk }), [openMeeting, openBulk]);

  useEffect(() => {
    let alive = true;
    fetchSiteContent().then((c) => { if (alive) setSiteContent(c || {}); }).catch(() => {});
    setPageMeta({
      title: "Our Story — Utsah Corporate Gifting",
      description: "The story behind Utsah: thoughtful corporate gifting, tree-planting impact, and our journey to 300+ Reliance Smart Bazaar stores.",
      canonicalPath: "/our-story",
    });
    return () => { alive = false; };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="utsah-app">
      <Nav onBookMeeting={openMeeting} />
      <Hero onReadOn={() => scrollTo("founders-note")} siteContent={siteContent} />
      <FoundersNote siteContent={siteContent} />
      <RelianceShowcase siteContent={siteContent} />
      <LookingAhead onExplore={() => navigate("/diwali-collection")} />
      <Footer handlers={handlers} />
      <WhatsAppSticky />
      <LeadDialog open={dialog.open} type={dialog.type} source="Our Story" onOpenChange={(v) => (v ? null : closeDialog())} />
      <Toaster position="bottom-center" richColors theme="light" toastOptions={{ style: { fontFamily: "Manrope, system-ui, sans-serif", fontWeight: 500 } }} />
    </div>
  );
}

/* ---------- 1. HERO ---------- */
function Hero({ onReadOn, siteContent }) {
  const desktopImg = siteContent?.our_story_hero || "";
  const mobileImg = siteContent?.our_story_hero_mobile_image || "";
  const hasImage = Boolean(desktopImg || mobileImg);
  return (
    <section className="section os-hero" data-testid="os-hero-section" style={{ paddingTop: 40 }}>
      <Reveal className="os-hero-media" data-testid="os-hero-image">
        {hasImage ? (
          <picture className="os-hero-picture">
            {mobileImg ? <source media="(max-width: 720px)" srcSet={assetUrl(mobileImg)} /> : null}
            <img src={assetUrl(desktopImg || mobileImg)} alt="Utsah — Editorial hero" />
          </picture>
        ) : (
          <>
            <svg width="56" height="56" viewBox="0 0 64 64" fill="none" aria-hidden>
              <rect x="6" y="12" width="52" height="40" rx="3" fill="var(--u-orange)" opacity="0.22" />
              <circle cx="22" cy="28" r="6" fill="var(--u-orange)" opacity="0.28" />
              <path d="M52 44L40 30L28 44" stroke="var(--u-orange)" strokeWidth="2" opacity="0.32" />
            </svg>
            <div className="add-image-label">Editorial Hero Image — Full Width Narrative</div>
          </>
        )}
      </Reveal>

      <div className="os-hero-intro">
        <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Our Story</Reveal>
        <Reveal as="h1" delay={1} className="section-headline" style={{ textAlign: "center" }}>
          Celebrate <em>Every Moment.</em>
        </Reveal>
        <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20, marginLeft: "auto", marginRight: "auto", textAlign: "center", maxWidth: 720 }}>
          At Utsah, we believe every celebration deserves to be remembered. We create thoughtful gifting experiences that help organisations celebrate, appreciate, and strengthen the relationships that matter most.
          <br /><br />
          Every gift is thoughtfully curated to reflect the emotion behind the occasion—whether it's welcoming a new employee, recognising a milestone, thanking a client, or celebrating a festival.
          <br /><br />
          It's our way of helping businesses turn moments of appreciation into lasting memories.
        </Reveal>
        <Reveal delay={3}>
          <button type="button" className="os-scroll-indicator" onClick={onReadOn} data-testid="os-read-on-btn">
            Read On
            <span aria-hidden>↓</span>
          </button>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- 2. FOUNDER'S NOTE ---------- */
function FoundersNote({ siteContent }) {
  const img = siteContent?.our_story_founder || "";
  return (
    <section className="section os-founders-note" id="founders-note" data-testid="os-founders-section">
      <div className="section-inner os-founder-grid">
        <div>
          <Reveal className="eyebrow">Founder's Note</Reveal>
          <Reveal as="h2" delay={1} className="section-headline">
            Why I Started <em>Utsah.</em>
          </Reveal>
          <Reveal as="div" delay={2} className="section-sub" style={{ marginTop: 20, maxWidth: 560 }}>
            <p style={{ marginBottom: 20 }}>
              When I started Utsah, I wasn't trying to build another gifting company. I wanted to build a brand that helps organisations celebrate people more thoughtfully.
            </p>
            <p style={{ marginBottom: 20 }}>
              Every gift carries an emotion—whether it's appreciation, gratitude, congratulations, or celebration. I've always believed that a thoughtful gift should preserve that emotion and pass it on to the person receiving it. From the beginning, the vision has been simple: to build a place where every celebration can find a meaningful gift.
            </p>
            <p style={{ marginBottom: 20 }}>
              I've also always believed that businesses should leave the world better than they found it. Growing trees has long been a personal passion of mine, so I wanted Utsah to create a way for companies to celebrate their people while creating something positive for the environment at the same time.
            </p>
            <p style={{ marginBottom: 20 }}>
              That's what Utsah means to me—celebrating people, strengthening relationships, and creating a positive impact beyond the gift itself.
            </p>
            <p style={{ marginBottom: 0 }}>
              Thank you for taking the time to learn our story.
            </p>
          </Reveal>
          <Reveal delay={3} className="os-founder-signature">
            <div className="os-founder-name">Atishay Kasliwal</div>
            <div className="os-founder-title">Founder, Utsah</div>
          </Reveal>
        </div>

        <Reveal delay={1} className="s3-image-placeholder os-founder-media" style={bgStyle(img)} data-testid="os-founder-image">
          {!img ? (
            <>
              <svg width="56" height="56" viewBox="0 0 64 64" fill="none" aria-hidden>
                <path d="M32 16 C22 16 16 22 16 32 C16 42 22 48 32 48 C42 48 48 42 48 32 C48 22 42 16 32 16 Z" fill="var(--u-orange)" opacity="0.22" />
                <circle cx="32" cy="28" r="8" fill="var(--u-orange)" opacity="0.28" />
              </svg>
              <div className="add-image-label">Editorial Feature — Founder Portrait</div>
            </>
          ) : null}
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- 3. RELIANCE SHOWCASE ---------- */
function RelianceShowcase({ siteContent }) {
  const g1 = siteContent?.our_story_gallery_1 || "";
  const g2 = siteContent?.our_story_gallery_2 || "";
  const g3 = siteContent?.our_story_gallery_3 || "";
  return (
    <section className="section os-gallery" data-testid="os-gallery-section">
      <div className="section-inner">
        <div className="os-gallery-intro">
          <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>A Milestone We'll Never Forget</Reveal>
          <Reveal as="h2" delay={1} className="section-headline" style={{ textAlign: "center" }}>
            From a simple idea to <em>300+ Reliance Smart Bazaar stores.</em>
          </Reveal>
          <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20, marginLeft: "auto", marginRight: "auto", textAlign: "center", maxWidth: 780 }}>
            From the very beginning, there was one milestone we hoped to achieve—to see a Utsah product on the shelves of one of India's largest retail chains. That vision became a reality during Diwali 2025, when our private-label festive collection, True Gesture, launched across 300+ Reliance Smart Bazaar stores across Central India, bringing our premium dry fruit gift boxes to thousands of customers.
            <br /><br />
            The collection went on to become the second highest-selling dry fruit gift brand in Reliance Smart Bazaar. For us, it wasn't just a retail launch—it was proof that thoughtful gifting could resonate at scale.
          </Reveal>
        </div>

        <div className="os-gallery-grid">
          <Reveal className="s3-image-placeholder os-gallery-large" style={bgStyle(g1)} data-testid="os-gallery-1">
            {!g1 ? <div className="add-image-label">Image 1</div> : null}
          </Reveal>
          <div className="os-gallery-stack">
            <Reveal delay={1} className="s3-image-placeholder" style={bgStyle(g2)} data-testid="os-gallery-2">
              {!g2 ? <div className="add-image-label">Image 2</div> : null}
            </Reveal>
            <Reveal delay={2} className="s3-image-placeholder" style={bgStyle(g3)} data-testid="os-gallery-3">
              {!g3 ? <div className="add-image-label">Image 3</div> : null}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- 4. LOOKING AHEAD ---------- */
const VISION = [
  { title: "30,000", body: "Trees to be planted in 2026" },
  { title: "300,000", body: "Trees across India by 2030" },
  { title: "India's Largest", body: "Thoughtful Gifting Company" },
];
function LookingAhead({ onExplore }) {
  return (
    <section className="section os-looking-ahead" data-testid="os-looking-ahead-section">
      <div className="section-inner">
        <div className="os-la-header">
          <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Looking Ahead</Reveal>
          <Reveal as="h2" delay={1} className="section-headline" style={{ textAlign: "center" }}>
            We're only <em>getting started.</em>
          </Reveal>
          <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20, marginLeft: "auto", marginRight: "auto", textAlign: "center", maxWidth: 760 }}>
            The milestones we've celebrated so far are only the beginning. Our ambition is to build India's most thoughtful gifting company—one that helps organisations celebrate people while creating a lasting positive impact on the world around us.
          </Reveal>
        </div>

        <div className="os-vision-grid" data-testid="os-vision-grid">
          {VISION.map((v, i) => (
            <Reveal key={v.title} delay={i} className="os-vision-card" data-testid={`os-vision-card-${i}`}>
              <div className="os-vision-num">{v.title}</div>
              <div className="os-vision-body">{v.body}</div>
            </Reveal>
          ))}
        </div>

        <Reveal className="os-closing">
          If that vision resonates with you, we'd love for you to be part of the journey.
        </Reveal>
        <Reveal delay={1} style={{ textAlign: "center" }}>
          <button className="btn-primary" onClick={onExplore} data-testid="os-explore-diwali-btn">Explore our Diwali Gifts</button>
        </Reveal>
      </div>
    </section>
  );
}
